import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { BeneficialInterest, BeneficialOwnerInput } from '@kanos/domain';
import { ChoiceCards, ChoiceChips, FieldGroup, NumberField } from '@kanos/ui';
import { useState } from 'react';
import { CountryField } from './country-field.js';
import { personRowsClassName, usePersonFields } from './person-fields.js';
import { usePepFields } from './pep-fields.js';
const shape = BeneficialOwnerInput.shape;
/** Why someone counts, as cards: a short name and when it applies (§ 3 Abs. 2 GwG). */
const INTEREST_CARDS = {
    ownership: { label: 'Kapitalanteile', hint: 'Mehr als 25 % am Kapital' },
    voting_rights: { label: 'Stimmrechte', hint: 'Mehr als 25 % der Stimmrechte' },
    control: { label: 'Kontrolle auf andere Weise', hint: 'z. B. über Treuhand oder Vetorechte' },
    fictitious: { label: 'Gesetzlicher Vertreter', hint: 'Wenn sonst niemand feststellbar ist' },
};
const interests = BeneficialInterest.options.map((id) => ({ id, ...INTEREST_CARDS[id] }));
/** The shares most often named, a press away. */
const SHARES = [25, 50, 75, 100];
/**
 * The fields of a beneficial owner - name, birth, countries, share and why, and whether the
 * person is politically exposed - for the firm app and the portal alike: `values` for the
 * request, `fields` for the form.
 */
export function useOwnerFields(owner) {
    const person = usePersonFields(owner, shape.givenName);
    const [nationality, setNationality] = useState(owner?.nationality ?? '');
    const [residenceCountry, setResidenceCountry] = useState(owner?.residenceCountry ?? '');
    // A new owner starts at shares, the usual case (2026-11-03); the firm changes it where another
    // reason applies.
    const [interest, setInterest] = useState(owner ? (owner.interest ?? null) : 'ownership');
    const share = owner?.shareBasisPoints ?? null;
    const [percent, setPercent] = useState(share === null ? null : share / 100);
    const pep = usePepFields(owner?.pep ?? null, 'person', 3);
    const values = {
        ...person.values,
        nationality: nationality || null,
        residenceCountry: residenceCountry || null,
        interest,
        shareBasisPoints: percent === null ? null : Math.round(percent * 100),
        pep: pep.values,
    };
    // Numbered sections: the person, why they count, whether politically exposed. Short fields
    // share a row - the names two to a row, birth and the countries three.
    const fields = (_jsxs(_Fragment, { children: [_jsx(FieldGroup, { title: "Person", step: 1, children: _jsxs("div", { className: personRowsClassName, children: [person.fields, _jsx(CountryField, { label: "Staatsangeh\u00F6rigkeit", value: nationality, onChange: setNationality }), _jsx(CountryField, { label: "Wohnsitzland", value: residenceCountry, onChange: setResidenceCountry })] }) }), _jsxs(FieldGroup, { title: "Berechtigung", step: 2, children: [_jsx(ChoiceCards, { label: "Grund", options: interests, value: interest, onChange: setInterest, columns: 2, isRequired: true }), _jsxs("div", { className: "flex flex-wrap items-end gap-3", children: [_jsx(NumberField, { className: "w-44", label: "Anteil in %", placeholder: "z. B. 25", value: percent, onChange: setPercent, minValue: 0, maxValue: 100, maximumFractionDigits: 2 }), _jsx(ChoiceChips, { label: "Anteil w\u00E4hlen", className: "pb-2", options: SHARES.map((n) => ({ id: String(n), label: `${String(n)} %` })), value: SHARES.includes(percent ?? -1) ? String(percent) : null, onChange: (n) => setPercent(Number(n)) })] }), _jsx("p", { className: "-mt-2 px-1 text-xs text-muted", children: "Kapital- oder Stimmanteil; leer, wenn nicht bekannt." })] }), pep.fields] }));
    return { values, fields };
}
//# sourceMappingURL=owner-fields.js.map